import { useTranslation } from "react-i18next";
import { type ConnectionState, useConnection } from "../../lib/api/connection";

/**
 * The connection light, with words (never colour alone): online is a round
 * dot, "server busy" a diamond, offline a ring with a slash — three shapes as
 * well as three colours. `board` draws the bright on-blue variants (session
 * screens); `paper` the top bar's. On a phone the word is visually hidden
 * (the shape still differs, the title and screen readers keep the word).
 * `role="status"` makes a change audible without stealing focus.
 */
const BOARD: Record<ConnectionState, { dot: string; ring: string; text: string }> = {
	online: { dot: "size-[0.75rem] rounded-full bg-[#3ddc97]", ring: "", text: "text-white" },
	degraded: { dot: "size-[0.75rem] rotate-45 rounded-[0.125rem] bg-cursor", ring: "", text: "text-white" },
	offline: { dot: "", ring: "#ff6b70", text: "text-white" },
};
const PAPER: Record<ConnectionState, { dot: string; ring: string; text: string }> = {
	online: { dot: "size-[0.5rem] rounded-full bg-done shadow-[0_0_0_3px_var(--color-done-wash)]", ring: "", text: "text-done" },
	degraded: { dot: "size-[0.625rem] rotate-45 rounded-[0.125rem] bg-wait", ring: "", text: "text-wait" },
	offline: { dot: "", ring: "var(--color-live)", text: "text-live" },
};

export function ConnectionLight({ tone = "board" }: { tone?: "board" | "paper" }) {
	const { t } = useTranslation();
	const state = useConnection();
	const look = (tone === "board" ? BOARD : PAPER)[state];
	return (
		<div
			role="status"
			title={t(`connection.${state}Hint`)}
			className={`flex min-h-touch items-center gap-[0.5rem] rounded-ctl px-[0.25rem] text-[0.875rem] ${look.text}`}
		>
			<span className="relative flex size-[0.75rem] items-center justify-center" aria-hidden="true">
				{state === "offline" ? (
					<svg viewBox="0 0 12 12" className="size-[0.75rem]" fill="none">
						<circle cx="6" cy="6" r="4.5" stroke={look.ring} strokeWidth="2" />
						<path d="M2.5 9.5 9.5 2.5" stroke={look.ring} strokeWidth="2" />
					</svg>
				) : (
					<span className={look.dot} />
				)}
			</span>
			<span className={`font-bold ${tone === "paper" ? "max-sm:sr-only" : ""}`}>{t(`connection.${state}`)}</span>
		</div>
	);
}
